import Close from '@mui/icons-material/Close';
import { Box, Stack, Switch } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useFetcher, useLocation, useMatches, useNavigate, useSearchParams, useNavigation } from '@remix-run/react';
import * as React from 'react';
import { AUTOMOCKUPS_PARAM, LOGO_PARAM } from '~/utils/searchParams';
import { StarSetIcon } from './AutomockupButton';
import AutomockupCarousel from './AutomockupCarousel';
import AutoMockupLastStep from './AutomockupLastStep';
import UploadAutomockupLogo from './UploadAutomockup';

const STEPS = {
  LANDING: 'landing',
  UPLOAD: 'upload',
  FINISHED: 'finished',
};

export function AutoMockupContainer({
  onCloseAutomockup,
  initialLogos,
  isLoadingData,
  detectedCompany,
}: {
  initialLogos: string[];
  isLoadingData: boolean;
  detectedCompany: { name: string; domain: string };
  onCloseAutomockup: () => void;
}) {
  const fetcher = useFetcher();
  const submit = fetcher.submit;
  const navigation = useNavigation();
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();
  let [searchParams, setSearchParams] = useSearchParams();

  const isSubmitting = navigation.state === 'submitting';
  const shouldRedirect = !(location.pathname.includes('bulk-swag') || location.pathname.includes('build-pack'));

  const [userSelectedLogo, setUserSelectedLogo] = React.useState<string | null>(initialLogos?.[0]);

  const [uploadLogos, setUploadLogos] = React.useState(false);

  React.useEffect(() => {
    if (initialLogos && initialLogos.length > 0 && !userSelectedLogo) {
      setUserSelectedLogo(initialLogos[0]);
    }
  }, [initialLogos, userSelectedLogo]);

  const automockupLogoUrl = searchParams.get(LOGO_PARAM);
  const automockupIsActive = searchParams.get(AUTOMOCKUPS_PARAM) === 'true';

  // CONDITIONS FOR STEPS
  let currentStep = STEPS.LANDING;

  if (automockupLogoUrl) {
    currentStep = STEPS.FINISHED;
    if (uploadLogos) {
      setUploadLogos(false);
    }
  } else if (
    uploadLogos ||
    (initialLogos?.length === 0 && !detectedCompany) ||
    (!initialLogos && !detectedCompany && !uploadLogos)
  ) {
    currentStep = STEPS.UPLOAD;
  }

  const companyName = matches[1]?.data?.user?.current_account?.name || detectedCompany?.name || '';

  const handleCarouselChange = (logoUrl: string) => {
    setUserSelectedLogo(logoUrl);
  };

  const handleSubmit = () => {
    if (userSelectedLogo) {
      searchParams.set(LOGO_PARAM, userSelectedLogo);
      searchParams.set(AUTOMOCKUPS_PARAM, 'true');
      setSearchParams(searchParams, { preventScrollReset: true });
      if (shouldRedirect) {
        submit(
          { _action: 'keep-modal-open', value: 'true' },
          { action: '/api/artworks', method: 'post', replace: true }
        );

        const url = '/shop/bulk-swag?' + searchParams.toString();
        navigate(url);
      }
    }
  };

  const handleActivateLogo = (value: boolean) => {
    if (value) {
      searchParams.set(AUTOMOCKUPS_PARAM, 'true');
      setSearchParams(searchParams, { preventScrollReset: true });
      if (shouldRedirect) {
        const url = '/shop/bulk-swag?' + searchParams.toString();
        navigate(url);
      }
    } else {
      if (searchParams.has(LOGO_PARAM)) {
        searchParams.set(AUTOMOCKUPS_PARAM, 'false');
        setSearchParams(searchParams, { preventScrollReset: true });
      }
    }
  };

  const handleChangeLogo = () => {
    searchParams.delete(LOGO_PARAM);
    searchParams.delete(AUTOMOCKUPS_PARAM);
    setSearchParams(searchParams, { preventScrollReset: true });
    setUserSelectedLogo(initialLogos[0]);
  };

  return (
    <>
      <Box
        sx={{
          borderRadius: 2,
          position: 'relative',
          bgcolor: 'background.paper',
          boxShadow: '0px 4px 8px 1px #00000014',
        }}
      >
        <IconButton
          onClick={onCloseAutomockup}
          sx={{ position: 'absolute', inset: '0px 0px auto auto', zIndex: 1301, m: 2 }}
        >
          <Close />
        </IconButton>
        <Stack p={3} position="relative">
          <Stack flexDirection="row" alignItems="center">
            <StarSetIcon />
            <Typography variant="h6" fontWeight={500} color="#71718B" ml={2}>
              Your company logo
            </Typography>
          </Stack>
          {/* CONTENT TO DISPLAY */}
          {isLoadingData || !initialLogos ? (
            <Stack
              width={360}
              height={460}
              justifyContent="center"
              alignItems="center"
              sx={{
                '& > *:nth-child(n+1)': {
                  mt: 2,
                },
              }}
            >
              <Skeleton variant="rectangular" width={140} height={140} />
              <Skeleton variant="rectangular" width="100%" height={50} />
              <Skeleton variant="rectangular" width="50%" height={20} />
              <Skeleton variant="rectangular" width="100%" height="50%" />
            </Stack>
          ) : (
            <>
              <Stack width={360} justifyContent="center" alignItems="center">
                {currentStep === STEPS.LANDING ? (
                  <AutomockupCarousel
                    handleChange={handleCarouselChange}
                    isSubmitting={isSubmitting}
                    logos={initialLogos}
                    currentMockupLogo={automockupLogoUrl}
                    message={
                      <Typography variant="subtitle1" mt={6} mb={4}>
                        <b>Welcome! </b>
                        We’ve noticed that you’re from {companyName}. Would you like to view our catalog with your
                        company’s logo?
                      </Typography>
                    }
                  />
                ) : currentStep === STEPS.UPLOAD ? (
                  <UploadAutomockupLogo />
                ) : currentStep === STEPS.FINISHED && automockupLogoUrl ? (
                  <AutoMockupLastStep
                    currentMockupLogo={automockupLogoUrl}
                    handleCarouselChange={handleCarouselChange}
                    isSubmitting={isSubmitting}
                  />
                ) : null}
              </Stack>

              <Stack flexDirection="row" justifyContent="space-between" alignItems="center" width="100%" mt={2}>
                {currentStep === STEPS.LANDING && (
                  <Button
                    onClick={() => setUploadLogos(true)}
                    variant="text"
                    sx={{ textTransform: 'uppercase', zIndex: 1301 }}
                  >
                    not my logo
                  </Button>
                )}
                {currentStep === STEPS.FINISHED && automockupLogoUrl ? (
                  <Button
                    disabled={isSubmitting}
                    onClick={handleChangeLogo}
                    variant="text"
                    sx={{ textTransform: 'uppercase', zIndex: 1301 }}
                  >
                    Change logo
                  </Button>
                ) : (
                  currentStep === STEPS.UPLOAD &&
                  initialLogos?.length > 0 && (
                    <Button
                      disabled={isSubmitting}
                      onClick={() => setUploadLogos(false)}
                      variant="text"
                      sx={{ textTransform: 'uppercase', zIndex: 1301 }}
                    >
                      Back
                    </Button>
                  )
                )}
                {currentStep === STEPS.FINISHED && automockupLogoUrl ? (
                  <Stack flexDirection="row" justifyContent="center" alignItems="center">
                    <Switch
                      checked={automockupIsActive}
                      disabled={isSubmitting}
                      onChange={(e) => handleActivateLogo(e.target.checked)}
                    />
                    <Typography ml={0.5} variant="body1">
                      Show logo
                    </Typography>
                  </Stack>
                ) : (
                  currentStep !== STEPS.UPLOAD && (
                    <Button
                      disabled={isSubmitting || automockupLogoUrl === userSelectedLogo}
                      onClick={() => handleSubmit()}
                      variant="contained"
                      sx={{ textTransform: 'uppercase', ml: !automockupLogoUrl ? 'auto' : 2, zIndex: 1301 }}
                    >
                      let’s go!
                    </Button>
                  )
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
}
